import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { useEscapeClose } from '#/shared/lib/hooks'
import { Calendar } from '#/shared/ui/calendar'
import { CloseSmallIcon } from '#/shared/ui/icons'

/** Earliest month the calendar can page back to. */
const MIN_MONTH = new Date(1960, 0)

/**
 * Modal over the chat for picking a single "show messages from this date".
 * Rendered always-mounted; `seed` (the tapped separator's day, ISO) gates it —
 * `null` renders nothing. Picking a day applies the filter and closes.
 */
export function ChatDateFilterModal({
  seed,
  onApply,
  onClose,
}: {
  seed: string | null
  onApply: (date: Date) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  useEscapeClose(onClose)
  if (seed === null) return null
  const seeded = dayjs(seed).toDate()
  // No messages exist in the future, so block dates after today.
  const today = dayjs().startOf('day').toDate()
  return (
    <div
      data-phone-surface=""
      className="absolute inset-0 z-40 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label={t('phonePanel.conversation.dateFilterClose')}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-gray-900/20"
      />
      <div className="relative w-fit max-w-full overflow-hidden rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-popover">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <span className="text-sm font-semibold">
            {t('phonePanel.conversation.dateFilterTitle')}
          </span>
          <button
            type="button"
            aria-label={t('phonePanel.conversation.dateFilterClose')}
            onClick={onClose}
            className="flex size-6 cursor-pointer items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          >
            <CloseSmallIcon className="size-4" />
          </button>
        </div>
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={seeded}
          defaultMonth={seeded}
          onSelect={(day) => day && onApply(day)}
          startMonth={MIN_MONTH}
          endMonth={today}
          disabled={{ after: today }}
          autoFocus
        />
      </div>
    </div>
  )
}
